/**
 * Simple D.R.Y. method to join an array of strings into a single string, each item separated by a space.  Will
 * automatically filter out any falsy (i.e. `undefined`, `null`, or empty strings) prior to joining.  Intended to ease
 * applying multiple classes - possibly conditionally - to elements.
 *
 * @example
 * const someVar = "XYZ";
 * stringJoin("abc", "def", test ? 'included' : null, someVar);
 * // if `test` is truthy, returns
 * "abc def included XYZ"
 * // else returns
 * "abc def XZY"
 *
 * @returns {String} Returns a single string comprised of the input items joined by an empty space, with falsy items excluded.
 */
export function stringJoin(...strings: (string | undefined | null)[]): string {
    return strings.filter((s) => !!s).join(" ");
}

export function seasonalContent(seasonStart?: string, seasonEnd?: string, title: string = "Product", discontinued?: boolean) {
    const today = new Date();
    let seasonalDetails = "";
    let seasonalLabel = "Out of season";
    let currentlyAvailable = false;

    if (discontinued) {
        seasonalLabel = "Limited time offering";
        seasonalDetails = `No longer available`;
        currentlyAvailable = false;
        return { seasonalLabel, seasonalDetails, currentlyAvailable };
    }

    if (seasonStart) {
        const seasonStartDate = isNaN(Date.parse(seasonStart)) ? null : new Date(seasonStart);
        const seasonEndDate = seasonEnd && !isNaN(Date.parse(seasonEnd)) ? new Date(seasonEnd) : null;

        if (seasonStartDate && seasonEndDate) {
            const startString = seasonStartDate.toLocaleString("default", { month: "long" });
            const endString = seasonEndDate.toLocaleString("default", { month: "long" });
            //if the season has already ended
            if (today > seasonEndDate) {
                seasonalDetails = `Returns in ${startString}.`;
            }
            //if the season is later this year
            else if (today < seasonStartDate) {
                seasonalDetails = `Our first shipment goes out ${startString}.`;
            }
            //else we're in the middle of the season
            else {
                currentlyAvailable = true;
                seasonalLabel = "Seasonal Item";
                seasonalDetails = `Available from ${startString} to ${endString} while supplies last.`;
            }
        }
        //if seasonStartDate can't be parsed as a dateTime Object, assume it's a seasonal product, but we don't know when it's coming back
        else {
            seasonalDetails = "Check later for more details.";
        }
    }
    //if we don't have a season, assume it's a year-round product
    else {
        currentlyAvailable = true;
        seasonalLabel = "Year-round Favorite!";
        seasonalDetails = `${title} is available at select retailers all year.`;
    }

    return { seasonalLabel, seasonalDetails, currentlyAvailable };
}
